import { useEffect } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "store";
import {
  setHostGames,
  setWhitelist,
  setBlacklist,
} from "store/actions";

import { db } from "config/firebase";
import {
  doc,
  collection,
  onSnapshot,
  where,
  query,
  Unsubscribe,
} from "firebase/firestore";

interface UseHostListenersProps {
  userId: string | null;
  isHost: boolean;
}

/**
 * Custom hook that manages all host-specific Firebase listeners
 * Automatically handles cleanup when component unmounts or userId/isHost changes
 */
export const useHostListeners = ({ userId, isHost }: UseHostListenersProps) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!userId || !isHost) return;

    const unsubscribes: Unsubscribe[] = [];

    // Host games listener
    const hostGamesQuery = query(
      collection(db, "games"),
      where("hostId", "==", userId),
    );

    const hostGamesUnsubscribe = onSnapshot(
      hostGamesQuery,
      (snapshot) => {
        const gamesList: Record<string, any> = {};
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          gamesList[data.id] = { ...data };
        });
        dispatch(setHostGames(gamesList));
      },
      (error) => {
        console.error("Host games listener error:", error);
      },
    );
    unsubscribes.push(hostGamesUnsubscribe);

    // Host lists (whitelist/blacklist) listener
    const hostDocRef = doc(db, "hosts", userId);

    const hostListsUnsubscribe = onSnapshot(
      hostDocRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          dispatch(setWhitelist(data?.whitelist || {}));
          dispatch(setBlacklist(data?.blacklist || {}));
        } else {
          // Document doesn't exist, set empty lists
          dispatch(setWhitelist({}));
          dispatch(setBlacklist({}));
        }
      },
      (error) => {
        console.error("Host lists listener error:", error);
      },
    );
    unsubscribes.push(hostListsUnsubscribe);

    // Cleanup function
    return () => {
      console.log("Cleaning up host listeners");
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [userId, isHost, dispatch]);
};

export default useHostListeners;